'use client';

function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useState } from 'react';
import { InternalProvider, useCombinedRefs, useId } from '../../util';
import { TextFieldInputElement, TextFieldLabel, TextFieldContainer, TextFieldHelperText, TextFieldAssistiveHelperText, TextFieldAffixContainer, TextFieldInputWrapper, TextFieldLabelAndInputWrapper } from './elements';
import { Icon } from '../Icon';
import { PaymentBadge } from '../PaymentBadge';
export const TextField = /*#__PURE__*/React.forwardRef(({
  label,
  id: incomingId,
  name,
  value,
  disabled = false,
  helperText = '',
  assistiveHelperText = '',
  affixLeft,
  affixRight,
  affixLeftClickable = false,
  onAffixLeftClick,
  affixRightClickable = false,
  onAffixRightClick,
  containerClassName = '',
  inputClassName = '',
  labelClassName = '',
  helperTextClassName = '',
  fieldStatus = 'default',
  type = 'text',
  hideSpinButtons = false,
  affixPaymentBadge,
  affixPaymentBadgeAltText,
  affixRightIconClassName,
  ...restProps
}, fRef) => {
  const id = useId('text-field', incomingId);
  const showHelperText = !!helperText;
  const [hasFocus, setHasFocus] = useState(false);
  const innerRef = React.useRef();
  const combinedRef = useCombinedRefs(fRef, innerRef);
  const onBlur = e => {
    var _restProps$onBlur;
    setHasFocus(false);
    (_restProps$onBlur = restProps.onBlur) === null || _restProps$onBlur === void 0 ? void 0 : _restProps$onBlur.call(restProps, e);
  };
  const onFocus = e => {
    var _restProps$onFocus;
    setHasFocus(true);
    (_restProps$onFocus = restProps.onFocus) === null || _restProps$onFocus === void 0 ? void 0 : _restProps$onFocus.call(restProps, e);
  };
  const onClick = () => {
    innerRef.current.focus();
  };
  const ariaDescribedBy = [helperText && `${id}-helper-text`, assistiveHelperText && `${id}-assistive-helper-text`].filter(Boolean).join(' ');
  return /*#__PURE__*/React.createElement(InternalProvider, null, /*#__PURE__*/React.createElement(TextFieldContainer, {
    "data-aui": "text-field",
    id: `${id}-field-container`,
    key: `${id}-field-container`,
    className: containerClassName
  }, /*#__PURE__*/React.createElement(TextFieldInputWrapper, {
    type: type,
    hideSpinButtons: hideSpinButtons,
    onClick: onClick,
    fieldStatus: fieldStatus,
    hasIcon: !!affixRight || !!affixLeft || !!affixPaymentBadge,
    className: `${hasFocus ? 'has-focus' : ''} ${disabled ? 'is-disabled' : ''}`
  }, affixLeft && /*#__PURE__*/React.createElement(TextFieldAffixContainer, {
    fieldStatus: fieldStatus,
    affixClickable: !disabled && affixLeftClickable,
    affixSide: "left",
    onClick: onAffixLeftClick,
    disabled: disabled
  }, typeof affixLeft === 'string' ? /*#__PURE__*/React.createElement(Icon.SSR, {
    src: affixLeft,
    size: "large"
  }) : affixLeft), affixPaymentBadge && /*#__PURE__*/React.createElement(TextFieldAffixContainer, {
    fieldStatus: fieldStatus,
    affixClickable: false,
    affixSide: "left",
    disabled: disabled
  }, /*#__PURE__*/React.createElement(PaymentBadge, {
    disabled: disabled,
    badgeType: affixPaymentBadge,
    altText: affixPaymentBadgeAltText
  })), /*#__PURE__*/React.createElement(TextFieldLabelAndInputWrapper, {
    fieldStatus: fieldStatus
  }, /*#__PURE__*/React.createElement(TextFieldInputElement, _extends({}, restProps, {
    type: type,
    "aria-label": type === 'password' ? null : label,
    "aria-describedby": ariaDescribedBy,
    placeholder: " ",
    id: id,
    ref: combinedRef,
    name: name,
    disabled: disabled,
    fieldStatus: fieldStatus,
    showHelperText: showHelperText,
    className: inputClassName,
    value: value,
    onFocus: onFocus,
    onBlur: onBlur,
    hasIcon: !!affixRight || !!affixLeft
  })), /*#__PURE__*/React.createElement(TextFieldLabel, {
    htmlFor: id,
    fieldStatus: fieldStatus,
    className: labelClassName,
    hasIcon: !!affixRight || !!affixLeft
  }, label)), affixRight && /*#__PURE__*/React.createElement(TextFieldAffixContainer, {
    fieldStatus: fieldStatus,
    affixSide: "right",
    affixClickable: !disabled && affixRightClickable,
    onClick: onAffixRightClick,
    disabled: disabled
  }, typeof affixRight === 'string' ? /*#__PURE__*/React.createElement(Icon.SSR, {
    className: affixRightIconClassName,
    src: affixRight,
    size: "large"
  }) : affixRight)), showHelperText && /*#__PURE__*/React.createElement(TextFieldHelperText, {
    fieldStatus: fieldStatus,
    className: helperTextClassName,
    "data-testid": "helper-text",
    id: `${id}-helper-text`,
    role: fieldStatus !== 'default' ? 'alert' : undefined
  }, helperText), /*#__PURE__*/React.createElement(TextFieldAssistiveHelperText, {
    id: `${id}-assistive-helper-text`
  }, assistiveHelperText)));
});
TextField.defaultProps = {
  type: 'text'
};